// Shared searcher-location store for BluePages.
//
// Holds the visitor's currently selected location (browser-detected OR manually
// chosen from the in-browser OpenDB reference) and exposes the origin H3 cell
// used by candidate search. A manually selected location is persisted to
// localStorage so it survives reloads and is surfaced as a "recent" shortcut.
//
// Raw coordinates are never stored — only the resolved place name, its type,
// and the derived H3 origin cell.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { coordsToNearestPlace, type NamedPlace } from './places';
import { resolveSearcherOriginFromCoords } from '@/lib/api/geolocation';

export type LocationStatus = 'loading' | 'ready' | 'unavailable';
export type LocationSource = 'detected' | 'manual' | 'unavailable';

interface SavedLocation {
  name: string;
  placeType: string;
  h3Origin: string;
}

interface LocationContextValue {
  status: LocationStatus;
  source: LocationSource | null;
  name: string | null;
  placeType: string | null;
  h3Origin: string | null;
  distanceKm: number | null;
  recent: NamedPlace[];
  panelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  /** Choose a place from the in-browser reference and adopt it as the origin. */
  selectPlace: (place: NamedPlace) => Promise<void>;
  /** Re-run browser geolocation to detect the current position. */
  redetect: () => void;
}

const SAVED_KEY = 'bp:location';
const RECENT_KEY = 'bp:recent-locations';
const RECENT_MAX = 6;

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJSON(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable — non-fatal */
  }
}

const LocationContext = createContext<LocationContextValue | null>(null);

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<LocationStatus>('loading');
  const [source, setSource] = useState<LocationSource | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [placeType, setPlaceType] = useState<string | null>(null);
  const [h3Origin, setH3Origin] = useState<string | null>(null);
  const [distanceKm, setDistanceKm] = useState<number | null>(null);
  const [recent, setRecent] = useState<NamedPlace[]>(() => readJSON<NamedPlace[]>(RECENT_KEY, []));
  const [panelOpen, setPanelOpen] = useState(false);
  const cancelledRef = useRef(false);

  const detect = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setStatus('unavailable');
      setSource('unavailable');
      return;
    }
    setStatus('loading');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        if (cancelledRef.current) return;
        try {
          const place = await coordsToNearestPlace(pos.coords.latitude, pos.coords.longitude);
          if (cancelledRef.current) return;
          if (place) {
            const origin = await resolveSearcherOriginFromCoords(place.latitude, place.longitude);
            if (cancelledRef.current) return;
            setName(place.name);
            setPlaceType(place.placeType);
            setH3Origin(origin.h3Origin ?? null);
            setDistanceKm(place.distanceKm);
            setSource('detected');
            setStatus('ready');
          } else {
            setStatus('unavailable');
            setSource('unavailable');
          }
        } catch {
          if (!cancelledRef.current) {
            setStatus('unavailable');
            setSource('unavailable');
          }
        }
      },
      () => {
        if (!cancelledRef.current) {
          setStatus('unavailable');
          setSource('unavailable');
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 },
    );
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    const saved = readJSON<SavedLocation | null>(SAVED_KEY, null);
    if (saved?.h3Origin) {
      setName(saved.name);
      setPlaceType(saved.placeType);
      setH3Origin(saved.h3Origin);
      setSource('manual');
      setStatus('ready');
    } else {
      detect();
    }
    return () => {
      cancelledRef.current = true;
    };
  }, [detect]);

  const selectPlace = useCallback(async (place: NamedPlace) => {
    const origin = await resolveSearcherOriginFromCoords(place.latitude, place.longitude);
    setName(place.name);
    setPlaceType(place.placeType);
    setH3Origin(origin.h3Origin ?? null);
    setDistanceKm(null);
    setSource('manual');
    setStatus('ready');
    writeJSON(SAVED_KEY, {
      name: place.name,
      placeType: place.placeType,
      h3Origin: origin.h3Origin ?? '',
    });
    setRecent((prev) => {
      const next = [place, ...prev.filter((p) => p.name !== place.name)].slice(0, RECENT_MAX);
      writeJSON(RECENT_KEY, next);
      return next;
    });
    setPanelOpen(false);
  }, []);

  const redetect = useCallback(() => {
    setPanelOpen(false);
    detect();
  }, [detect]);

  const value: LocationContextValue = {
    status,
    source,
    name,
    placeType,
    h3Origin,
    distanceKm,
    recent,
    panelOpen,
    setPanelOpen,
    selectPlace,
    redetect,
  };

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

export function useLocationContext(): LocationContextValue {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocationContext must be used within a LocationProvider');
  return ctx;
}
